import React from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Grid as MuiGrid,
  Typography
} from '@mui/material';
import { PAINT_COLORS, OBJECT_ITEMS } from '../data/paintObjects';
import ToolCard from './ToolCard';
import fogImg from '../assets/fog.jpg';
import { GGDialog, GGDialogTitle } from './shared/StyledDialogs';
import { useGame } from '../context/GameContext';

const PaintToolContent = ({ selectedColor, onColorSelect, onClose, isCreator }) => {
  // Add function to handle fog toggle
  const handleFogSelect = () => {
    // When selecting fog, only set fog without a color
    onColorSelect({
      fog: true
    });
  };

  // Add function to handle clear fog
  const handleClearFog = () => {
    onColorSelect({
      clearFog: true  // Special flag to indicate fog clearing
    });
  };

  // Add function to handle color select
  const handleColorSelect = (color) => {
    // Just set the color without fog
    onColorSelect(color);
  };

  // Add function to handle clear
  const handleClearSelect = () => {
    // Keep fog but clear the color
    onColorSelect({
      clearColor: true  // New flag to indicate color clearing only
    });
  };

  // Helper function to check if a color is selected
  const isColorSelected = (color) => {
    if (!selectedColor) return false;
    
    // Handle object case
    if (typeof selectedColor === 'object') {
      if (selectedColor.clearColor) return color === undefined;
      if (selectedColor.color) return selectedColor.color === color;
      return false;
    }
    
    // Handle direct color string case
    return selectedColor === color;
  };

  // Helper function to check if fog is selected
  const isFogSelected = () => {
    if (!selectedColor) return false;
    return typeof selectedColor === 'object' && selectedColor.fog;
  };

  // Helper function to check if clear fog is selected
  const isClearFogSelected = () => {
    if (!selectedColor) return false;
    return typeof selectedColor === 'object' && selectedColor.clearFog;
  };

  // Helper function to get the current tool name
  const getToolName = () => {
    if (!selectedColor) return 'Select tool';
    if (typeof selectedColor === 'object') {
      if (selectedColor.clearFog) return 'Clear Fog';
      if (selectedColor.fog) return 'Add Fog';
    }
    // Find the color name from both PAINT_COLORS and OBJECT_ITEMS
    const colorInfo = PAINT_COLORS.find(c => c.color === selectedColor) || 
                     OBJECT_ITEMS.find(o => o.color === selectedColor);
    return colorInfo ? colorInfo.name : 'Select a Color';
  };

  return (
    <GGDialog 
      open={true} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <GGDialogTitle>Paint Tool - {getToolName()}</GGDialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 650 }}>
          {/* Terrain Section */}
          <Divider sx={{ my: 1 }}>Terrain</Divider>
          <MuiGrid container spacing={1}>
            {PAINT_COLORS.map(({ name, color, preview, image }) => (
              <MuiGrid item xs={4} key={name}>
                <ToolCard
                  name={name}
                  color={color}
                  preview={image || preview}
                  selected={isColorSelected(color)}
                  onClick={() => handleColorSelect(color)}
                  gridItem
                />
              </MuiGrid>
            ))}
          </MuiGrid>

          {/* Objects Section */}
          <Divider sx={{ my: 1 }}>Objects</Divider>
          <MuiGrid container spacing={1}>
            {OBJECT_ITEMS.map(({ name, color, preview, image }) => (
              <MuiGrid item xs={4} key={name}>
                <ToolCard
                  name={name}
                  color={color}
                  preview={image || preview}
                  selected={isColorSelected(color)}
                  onClick={() => handleColorSelect(color)}
                  gridItem
                />
              </MuiGrid>
            ))}
          </MuiGrid>

          {/* Fog of War Section */}
          {isCreator && (
            <>
              <Divider sx={{ my: 1 }}>Fog of War</Divider>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                align="center"
                sx={{ 
                  mb: 1, 
                  px: 1,
                  fontStyle: 'italic'
                }}
              >
                Add fog then setup the area within. Other players will not see the area until the fog is cleared.
              </Typography>
              <ToolCard
                name="Add Fog"
                preview={fogImg}
                selected={isFogSelected()}
                onClick={handleFogSelect}
              />
            </>
          )}

          {/* Clear Tools Section */}
          <Divider sx={{ my: 1 }}>Clear Tools</Divider>
          <ToolCard
            name="Clear Terrain"
            selected={selectedColor?.clearColor || false}
            onClick={handleClearSelect}
          />

          {/* Clear Fog Section */}
          {isCreator && (
            <ToolCard
              name="Clear Fog"
              preview="linear-gradient(45deg, black 0%, transparent 100%)"
              selected={isClearFogSelected()}
              onClick={handleClearFog}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </GGDialog>
  );
};

const PaintTool = () => {
  const {
    showPaintModal,
    setShowPaintModal,
    selectedPaintColor: selectedColor,
    onPaintColorSelect: onColorSelect,
    isCreator,
  } = useGame();
  if (!showPaintModal) return null;
  return (
    <PaintToolContent
      selectedColor={selectedColor}
      onColorSelect={onColorSelect}
      onClose={() => setShowPaintModal(false)}
      isCreator={isCreator}
    />
  );
};

export default PaintTool;